// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


// ─── Closed Files page ────────────────────────────────────────────────────────
//
// Lists clients with case_status IN ('closed','settled'). Mirrors the shape
// of AdminClientsPage so users have a familiar interface, with these
// differences:
//   * The status filter pill at the top toggles between Closed, Settled, and
//     both. Default is "All Closed Files" (closed + settled).
//   * The status column shows Closed / Settled instead of Awaiting Login /
//     Active.
//   * Adds a "Closed on" column so you can scan for old files.
//   * Manage links route to the same client detail page as active clients.

function lastName(name) {
  const parts = (name || '').trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// 6 months in ms — mirror auth.js. Used to flag rows whose portal access
// has already expired so the user knows the client can't log in anymore.
const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 183;

function portalAccessRemaining(closedAtIso) {
  if (!closedAtIso) return null;
  const closedAt = new Date(closedAtIso).getTime();
  if (!Number.isFinite(closedAt)) return null;
  const remainingMs = SIX_MONTHS_MS - (Date.now() - closedAt);
  if (remainingMs <= 0) return { expired: true, label: 'Portal access expired' };
  const days = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
  return { expired: false, label: `Portal access: ${days} day${days !== 1 ? 's' : ''} left` };
}

function StatusBadge({ status }) {
  const styles = status === 'settled' ?
  { bg: '#f0f0f0', fg: '#555', border: '#dcdcdc', label: 'Settled' } :
  { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e', label: 'Closed' };
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3,
      background: styles.bg, color: styles.fg, border: `1px solid ${styles.border}`,
      whiteSpace: 'nowrap'
    }}>
      {styles.label}
    </span>);

}

export default function AdminClosedFilesPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('closed'); // closed_at DESC by default
  const [sortDir, setSortDir] = useState('desc');
  // 'all' (closed+settled), 'closed', or 'settled'
  const [statusFilter, setStatusFilter] = useState('all');


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const apiStatus =
    statusFilter === 'closed' ? 'closed' :
    statusFilter === 'settled' ? 'settled' :
    'closed_or_settled';
    adminApi.listClients(apiStatus).
    then((data) => {if (!cancelled) {setClients(data);setLoading(false);setError('');}}).
    catch((err) => {if (!cancelled) {setError(err.message);setLoading(false);}});
    return () => {cancelled = true;};
  }, [statusFilter]);

  function toggleSort(col) {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');else
    {setSortBy(col);setSortDir(col === 'closed' ? 'desc' : 'asc');}
  }

  // Search runs against real underlying values regardless of privacy mode
  // (same convention as AdminClientsPage).
  const filtered = clients.
  filter((c) => {
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.username || '').toLowerCase().includes(q) ||
      (c.clio_matter_number || '').toLowerCase().includes(q));

  }).
  sort((a, b) => {
    let va, vb;
    if (sortBy === 'lastname') {
      va = lastName(a.name);
      vb = lastName(b.name);
    } else if (sortBy === 'closed') {
      // Sort by closed_at, falling back to created_at so legacy rows
      // with no timestamp don't all sink to the bottom.
      va = new Date(a.closed_at || a.created_at || 0).getTime();
      vb = new Date(b.closed_at || b.created_at || 0).getTime();
    } else {
      va = new Date(a.created_at).getTime();
      vb = new Date(b.created_at).getTime();
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function PillButton({ value, label, count }) {
    const active = statusFilter === value;
    return (
      <button
        onClick={() => setStatusFilter(value)}
        className="btn btn-sm"
        style={{
          background: active ? '#0F1838' : '#fff',
          color: active ? '#fff' : '#555',
          borderColor: active ? '#0F1838' : '#ddd'
        }}>
        {label}{typeof count === 'number' ? ` (${count})` : ''}
      </button>);

  }

  // Counts shown on the filter pills come from the currently-loaded set
  // when statusFilter === 'all'. For 'closed' / 'settled' we'd need a
  // separate query to know the other count — not worth a round-trip; we
  // just omit counts when a filter is already active.
  const showCounts = statusFilter === 'all';
  const closedCount = clients.filter((c) => c.case_status === 'closed').length;
  const settledCount = clients.filter((c) => c.case_status === 'settled').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
        <div>
          <h1 className="page-title">Closed Files</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            {clients.length} file{clients.length !== 1 ? 's' : ''} in archive
            {' '}&middot;{' '}
            <Link to="/admin/clients" style={{ color: '#2f5496', textDecoration: 'none' }}>
              ← Back to Active Clients
            </Link>
          </p>
        </div>
      </div>

      {/* Filter pills + search */}
      <div style={{ display: 'flex', gap: 8, margin: '16px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <PillButton value="all" label="All" count={showCounts ? clients.length : undefined} />
        <PillButton value="closed" label="Closed" count={showCounts ? closedCount : undefined} />
        <PillButton value="settled" label="Settled" count={showCounts ? settledCount : undefined} />
        <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 180 }}>
          <input
            type="text"
            placeholder="Search by name, email, username, or matter #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ margin: 0 }} />
          
        </div>
        {search && <button className="btn btn-sm" onClick={() => setSearch('')}>Clear</button>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div>}

      {!loading && !error &&
      <>
          {search &&
        <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginBottom: 12 }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
            </p>
        }

          {filtered.length === 0 &&
        <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
              {search ? `No closed files match "${search}".` :
          statusFilter === 'closed' ? 'No closed cases yet.' :
          statusFilter === 'settled' ? 'No settled cases yet.' :
          'No closed files yet. Cases moved to Closed Files will appear here.'}
            </div>
        }

          {/* Desktop table */}
          <div className="desktop-only">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee', background: '#F5F1E8' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#555', cursor: 'pointer' }} onClick={() => toggleSort('lastname')}>
                      Name {sortBy === 'lastname' ? sortDir === 'asc' ? '↑' : '↓' : <span style={{ color: '#ccc' }}>↕</span>}
                    </th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#888', fontWeight: 'normal' }}>Matter #</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#888', fontWeight: 'normal' }}>Email</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#888', fontWeight: 'normal' }}>Status</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#555', cursor: 'pointer' }} onClick={() => toggleSort('closed')}>
                      Closed On {sortBy === 'closed' ? sortDir === 'asc' ? '↑' : '↓' : <span style={{ color: '#ccc' }}>↕</span>}
                    </th>
                    <th style={{ padding: '10px 16px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => {
                  const portal = portalAccessRemaining(c.closed_at);
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>
                          {c.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: '#555' }}>
                          {c.clio_matter_number || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555' }}>
                          {c.email?.includes('@pending.import') ?
                        <em style={{ color: '#aaa' }}>(no email)</em> :
                        c.email}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <StatusBadge status={c.case_status} />
                          {portal &&
                        <div style={{
                          fontSize: 10, fontFamily: 'Arial, sans-serif',
                          color: portal.expired ? '#B85042' : '#888',
                          marginTop: 4
                        }}>
                              {portal.label}
                            </div>
                        }
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888' }}>
                          {fmtDate(c.closed_at)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Link to={`/admin/clients/${c.id}`} className="btn btn-sm">Manage</Link>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only">
            {filtered.map((c) => {
            const portal = portalAccessRemaining(c.closed_at);
            return (
              <div key={c.id} className="card" style={{ padding: '16px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
                        {c.name}
                      </div>
                      {c.clio_matter_number &&
                    <div style={{ fontSize: 11, color: '#555', fontFamily: 'monospace', marginBottom: 2 }}>
                          {c.clio_matter_number}
                        </div>
                    }
                      <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginBottom: 8 }}>
                        Closed {fmtDate(c.closed_at)}
                      </div>
                      <StatusBadge status={c.case_status} />
                      {portal &&
                    <div style={{
                      fontSize: 10, fontFamily: 'Arial, sans-serif',
                      color: portal.expired ? '#B85042' : '#888',
                      marginTop: 6
                    }}>
                          {portal.label}
                        </div>
                    }
                    </div>
                    <Link to={`/admin/clients/${c.id}`} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                      Manage
                    </Link>
                  </div>
                </div>);

          })}
          </div>
        </>
      }
    </div>);

}